import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AppUtils from "@src/utils/appUtils";
import SignolUtils from "@src/utils/signolUtils";
import { authEndpoints } from "@src/services/endpoints";
import {
  signolImagesRepositoryURL,
  IDP_LOGIN_URL,
  UsernameTypes,
  EMAIL_SIGNOL_SUPPORT,
} from "@src/constants/constants";

import {
  Button,
  FormInput,
  ImageWithRetry,
  Loader,
} from "@src/components/atoms";
import Form from "@src/components/forms/Form";
import SecurityQuestionsModal from "@src/components/forms/SecurityQuestionsModal";
import {
  forgotPasswordEmailSchema,
  forgotPasswordUsernameSchema,
} from "@src/components/forms/formHelpers";

const envType = import.meta.env.VITE_REACT_APP_ENV;

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [usernameFieldError, setUsernameFieldError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState(null);
  const [isLoadingSecurityQuestions, setIsLoadingSecurityQuestions] =
    useState(false);

  const usernameType = searchParams.get("ulabel");
  const isEmailType = usernameType === "email";
  const usernameLabel = isEmailType ? "Email address" : "Username";
  const usernameInfo = isEmailType
    ? `This is the ${usernameLabel.toLowerCase()} associated with your Signol account.`
    : "This is the unique ID you used to register (e.g., discharge book number, employee ID, or username)";

  const fetchSecurityQuestions = async (username) => {
    setIsLoadingSecurityQuestions(true);
    setError("");
    setUsernameFieldError("");
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["GET_SECURITY_QUESTIONS", username],
        queryFn: () => authEndpoints.getOperatorSecurityQuestion(username),
        staleTime: 0, // do not cache this query
        throwOnError: true, // override global default so errors always reach the catch block
      });
      if (!data?.questionOptions) {
        setUsernameFieldError(
          "Username not recognised. Please check and try again.",
        );
        return;
      }
      setSecurityQuestions({ ...data, username });
    } catch (error) {
      console.error("Error fetching security questions:", error);
      if (
        error?.description === "Account Locked" ||
        error?.message === "Account Locked"
      ) {
        setError(
          `Account locked. Please contact ${EMAIL_SIGNOL_SUPPORT} for further assistance.`,
        );
      } else {
        setUsernameFieldError(
          "Username not recognised. Please check and try again.",
        );
      }
    } finally {
      setIsLoadingSecurityQuestions(false);
    }
  };

  const forgotPasswordMutation = useMutation({
    mutationKey: ["FORGOT_PASSWORD"],
    mutationFn: (data) => authEndpoints.requestForgotPassword(data),
    onSuccess: (res) => {
      if (res.data?.otp_token) {
        window.location.href = `/resetPassword?token=${res.data.otp_token}&redirectUrl=loginUsername`;
        return;
      }
      setSecurityQuestions(null);
      setSubmitted(!!res.result);
    },
    onError: (error) => {
      setSecurityQuestions(null);
      setError(
        error?.description ||
          error?.message ||
          `Please check if you have entered correctly your details, or try again later. If the issue persists, please contact ${EMAIL_SIGNOL_SUPPORT}`,
      );
    },
  });

  const handleSubmit = (data) => {
    setError("");
    const { username: submittedUsername } = data;

    if (isEmailType) {
      // email flow (aviation)
      const ssoCustomer = SignolUtils.getLoginSSO(submittedUsername);
      if (ssoCustomer) {
        setError(
          "Your email address belongs to an external identity provider. Please contact your manager to change your password.",
        );
        return;
      }
      forgotPasswordMutation.mutate({ username: submittedUsername });
    } else {
      // username flow with security questions (maritime)
      fetchSecurityQuestions(submittedUsername);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl mx-8 overflow-clip w-full max-w-md xl:max-w-lg">
        <div className="mx-auto py-6 px-8 bg-white">
          <div className="mx-auto py-8 w-40">
            <ImageWithRetry
              src={`${signolImagesRepositoryURL}/SignolLogoPrimary.png`}
              alt="Signol logo"
            />
            {envType !== "production" && (
              <span className="relative bottom-3 float-right font-bold text-pink-400">
                {envType === "dev" ? "qa" : envType}
              </span>
            )}
          </div>
          <div className="text-center space-y-4">
            <h2 className="text-xl font-bold text-green-600 mb-4">
              Check your inbox
            </h2>
            <p className="text-sm text-gray-700 mb-6">
              We've sent a link to your {usernameLabel.toLowerCase()} to reset
              your password.
            </p>
            <div className="flex justify-center">
              <Link
                to="/"
                className="text-primary-500 hover:font-bold underline"
              >
                Go to login page
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-2xl mx-8 overflow-clip w-full max-w-md xl:max-w-lg">
        <div className="mx-auto py-6 px-8 bg-white">
          <div className="mx-auto py-8 w-40">
            <ImageWithRetry
              src={`${signolImagesRepositoryURL}/SignolLogoPrimary.png`}
              alt="Signol logo"
            />
            {envType !== "production" && (
              <span className="relative bottom-3 float-right font-bold text-pink-400">
                {envType === "dev" ? "qa" : envType}
              </span>
            )}
          </div>

          <h3 className="text-2xl font-bold mb-2 text-center">
            Forgot Password
          </h3>

          <Form
            onSubmit={handleSubmit}
            schema={
              isEmailType
                ? forgotPasswordEmailSchema
                : forgotPasswordUsernameSchema
            }
            className="space-y-6"
          >
            {(methods) => (
              <>
                <FormInput
                  info={usernameInfo}
                  label={usernameLabel}
                  name="username"
                  type={isEmailType ? "email" : "text"}
                  placeholder={
                    isEmailType ? "example@company.com" : "Your username"
                  }
                  error={
                    methods.formState.errors.username?.message ||
                    usernameFieldError
                  }
                  validationMode="onChange"
                  textColor="primary"
                />

                <div className="flex justify-center">
                  <Button
                    submit
                    loading={
                      forgotPasswordMutation.isPending ||
                      isLoadingSecurityQuestions
                    }
                    disabled={
                      forgotPasswordMutation.isPending ||
                      isLoadingSecurityQuestions
                    }
                    className="px-8"
                  >
                    {isEmailType ? "Request reset link" : "Continue"}
                  </Button>
                </div>

                {error && (
                  <div className="mt-2 text-xs italic text-red-500 text-center">
                    {error}
                  </div>
                )}
              </>
            )}
          </Form>

          <div className="flex items-center justify-center py-6 text-sm text-gray-500">
            Remember your password?
            <div className="text-primary-500 text-center underline mx-2">
              <Link to="/" className="text-primary-500 hover:font-bold">
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Security Questions Modal - rendered outside main container */}
      {securityQuestions && (
        <SecurityQuestionsModal
          username={securityQuestions.username}
          usernameType={usernameLabel}
          verificationData={securityQuestions}
          onClose={() => setSecurityQuestions(null)}
          isSubmitting={forgotPasswordMutation.isPending}
          onSuccess={() => {
            forgotPasswordMutation.mutate({
              username: securityQuestions.username,
            });
          }}
        />
      )}
    </>
  );
};

export const forgotPasswordPageControls = {};

ForgotPasswordPage.propTypes = AppUtils.getPropTypeFromControls(
  forgotPasswordPageControls,
);

export default ForgotPasswordPage;
